/* Cellform — product page: variant selection, gallery, sticky ATC, delivery estimate. */
(() => {
  const theme = window.theme;

  /* ---------- Gallery ---------- */
  if (!customElements.get('media-gallery')) customElements.define('media-gallery', class extends HTMLElement {
    connectedCallback() {
      this.viewer = this.querySelector('.gallery__viewer');
      this.counter = this.querySelector('[data-gallery-index]');
      this.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-target]');
        if (thumb) { this.setActive(thumb.dataset.target); return; }
        const zoom = e.target.closest('[data-zoom]');
        if (zoom && window.matchMedia('(min-width: 990px)').matches) this.openZoom(zoom);
      });
      if (this.viewer && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => entries.forEach((en) => {
          if (en.isIntersecting && this.counter) this.counter.textContent = [...this.viewer.children].indexOf(en.target) + 1;
        }), { root: this.viewer, threshold: 0.6 });
        [...this.viewer.children].forEach((li) => io.observe(li));
      }
    }
    setActive(mediaId) {
      const item = this.viewer && this.viewer.querySelector(`[data-media-id="${mediaId}"]`);
      if (!item) return;
      this.viewer.querySelectorAll('.gallery__item').forEach((li) => li.classList.toggle('is-active', li === item));
      this.querySelectorAll('[data-target]').forEach((b) => (b.dataset.target === String(mediaId) ? b.setAttribute('aria-current', 'true') : b.removeAttribute('aria-current')));
      if (!window.matchMedia('(min-width: 990px)').matches) this.viewer.scrollTo({ left: item.offsetLeft - this.viewer.offsetLeft, behavior: 'smooth' });
    }
    openZoom(btn) {
      const dialog = document.querySelector('.zoom-dialog');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      const img = dialog.querySelector('[data-zoom-img]');
      img.src = btn.dataset.zoom;
      img.alt = (btn.querySelector('img') || {}).alt || '';
      dialog.showModal();
    }
  });

  /* ---------- Variant picker ---------- */
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', class extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.addEventListener('change', () => this.onChange());
    }
    selectedOptions() {
      return [...this.querySelectorAll('fieldset')].map((fs) => { const c = fs.querySelector('input:checked'); return c ? c.value : null; });
    }
    onChange() {
      const opts = this.selectedOptions();
      this.querySelectorAll('fieldset').forEach((fs, i) => { const s = fs.querySelector('[data-selected-value]'); if (s) s.textContent = opts[i]; });
      const variant = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant, focusId: document.activeElement && document.activeElement.id } }));
    }
  });

  /* ---------- Product info (orchestrates updates via Section Rendering API) ---------- */
  if (!customElements.get('product-info')) customElements.define('product-info', class extends HTMLElement {
    connectedCallback() {
      this.section = this.dataset.section;
      this.isMain = !this.closest('.quick-view');
      this.addEventListener('variant:change', (e) => this.onVariantChange(e.detail));
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-selling-plan-toggle]')) {
          const input = this.querySelector('[data-selling-plan-input]');
          if (input) input.value = e.target.value;
        }
      });
      this.initStickyAtc();
      this.initDelivery();
    }
    async onVariantChange({ variant, focusId }) {
      const idInput = this.querySelector('[data-variant-id]');
      const btn = this.querySelector(`#AddToCart-${this.section}`);
      if (!variant) {
        if (btn) { btn.disabled = true; btn.textContent = theme.strings.unavailable; }
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (variant.featured_media) {
        const gallery = this.querySelector('media-gallery');
        if (gallery) gallery.setActive(variant.featured_media.id);
      }
      if (this.isMain) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      const qty = this.querySelector('input[name="quantity"]');
      const qtyVal = qty ? qty.value : null;
      try {
        if (this.abort) this.abort.abort();
        this.abort = new AbortController();
        const html = await fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${this.section}`, { signal: this.abort.signal }).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        ['Price', 'BuyButtons', 'Delivery', 'Glance', 'ProductJSON', 'VariantPicker'].forEach((prefix) => {
          const id = `${prefix}-${this.section}`;
          const cur = document.getElementById(id);
          const next = doc.getElementById(id);
          if (cur && next) cur.replaceWith(next);
        });
        const sticky = document.getElementById(`StickyATC-${this.section}`);
        const nextSticky = doc.getElementById(`StickyATC-${this.section}`);
        if (sticky && nextSticky) sticky.querySelector('.sticky-atc__inner').replaceWith(nextSticky.querySelector('.sticky-atc__inner'));
        const newQty = this.querySelector('input[name="quantity"]');
        if (newQty && qtyVal) newQty.value = qtyVal;
        if (focusId) { const f = document.getElementById(focusId); if (f) f.focus(); }
        this.initDelivery();
        this.observeAtc();
      } catch (e) { /* aborted */ }
    }

    initStickyAtc() {
      this.sticky = document.getElementById(`StickyATC-${this.section}`);
      if (!this.sticky || !('IntersectionObserver' in window)) return;
      this.sticky.hidden = false;
      this.io = new IntersectionObserver(([entry]) => {
        // Visible whenever the main button is off-screen (above or below), so add to cart is always
        // reachable without scrolling on small viewports.
        const past = !entry.isIntersecting;
        this.sticky.classList.toggle('is-visible', past);
        this.sticky.setAttribute('aria-hidden', String(!past));
        this.sticky.querySelectorAll('button').forEach((b) => { b.tabIndex = past ? 0 : -1; });
      });
      this.observeAtc();
    }
    observeAtc() {
      if (!this.io) return;
      this.io.disconnect();
      const btn = document.getElementById(`AddToCart-${this.section}`);
      if (btn) this.io.observe(btn);
    }

    initDelivery() {
      const el = this.querySelector('delivery-estimate');
      if (el && el.update) el.update();
    }
  });

  /* ---------- Delivery estimate: "Order within 3h 20m for dispatch today" ---------- */
  if (!customElements.get('delivery-estimate')) customElements.define('delivery-estimate', class extends HTMLElement {
    connectedCallback() { this.update(); this.timer = setInterval(() => this.update(), 60000); }
    disconnectedCallback() { clearInterval(this.timer); }
    londonNow() {
      const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false, weekday: 'short'
      }).formatToParts(new Date()).map((p) => [p.type, p.value]));
      const days = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 0 };
      return { date: `${parts.year}-${parts.month}-${parts.day}`, dow: days[parts.weekday], minutes: (parseInt(parts.hour, 10) % 24) * 60 + parseInt(parts.minute, 10), y: +parts.year, m: +parts.month, d: +parts.day };
    }
    isDispatchDay(dateStr, dow) {
      const d = theme.dispatch;
      const days = String(d.days || '1,2,3,4,5').split(',').map(Number);
      const holidays = (d.holidays || []).map((h) => h.trim()).filter(Boolean);
      return days.includes(dow) && !holidays.includes(dateStr);
    }
    update() {
      const d = theme.dispatch || {};
      const m = /^(\d{1,2}):(\d{2})$/.exec(d.cutoff || '');
      const text = this.querySelector('[data-delivery-text]');
      if (!m || !text) { this.hidden = true; return; }
      const cutoff = parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
      const now = this.londonNow();
      if (this.isDispatchDay(now.date, now.dow) && now.minutes < cutoff) {
        const left = cutoff - now.minutes;
        const h = Math.floor(left / 60); const min = left % 60;
        const time = `${h > 0 ? `${h}${theme.strings.hours} ` : ''}${min}${theme.strings.minutes}`;
        text.innerHTML = theme.strings.dispatchToday.replace('[time]', `<strong>${time}</strong>`);
        this.hidden = false;
        return;
      }
      // Find next dispatch day (UTC date arithmetic on the London calendar date).
      const base = Date.UTC(now.y, now.m - 1, now.d);
      for (let i = 1; i <= 14; i += 1) {
        const dt = new Date(base + i * 864e5);
        const ds = dt.toISOString().slice(0, 10);
        if (this.isDispatchDay(ds, dt.getUTCDay())) {
          const label = new Intl.DateTimeFormat('en-GB', { weekday: 'long', timeZone: 'UTC' }).format(dt);
          text.innerHTML = theme.strings.dispatchNext.replace('[day]', `<strong>${label}</strong>`);
          this.hidden = false;
          return;
        }
      }
      this.hidden = true;
    }
  });
})();
